import { Box, Button, IconButton, Popover, Stack, TextField, Tooltip } from '@mui/material';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import type { KeyboardEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FIELD_HEIGHT_SX } from '../forms/FormField';
import { descriptionText, toEditorHtml } from '../state/detailsView';

import { DESCRIPTION_CONTENT_SX, DESCRIPTION_EXTENSIONS } from './extensions';

import { MaterialSymbol } from '@/components/MaterialSymbol';

/**
 * The description's editor (brief §3, decision 3, ADR 0052): a toolbar of
 * three — Bold, Bulleted list, Link — over a TipTap editor, in the library's
 * field look (1px border, radius 6, blue when focused).
 *
 * Link opens a small popover for the address rather than the browser's
 * prompt: it is styled, keyboard-friendly, and Esc in it closes the popover
 * without cancelling the card.
 */
export type RichTextEditorProps = {
  id: string;
  value: string;
  /** '' when the editor holds no words. */
  onChange: (html: string) => void;
  label: string;
  autoFocus?: boolean;
};

const withScheme = (raw: string): string => {
  const url = raw.trim();
  return /^[a-z][a-z0-9+.-]*:/i.test(url) ? url : `https://${url}`;
};

export const RichTextEditor = ({
  id,
  value,
  onChange,
  label,
  autoFocus = false,
}: RichTextEditorProps) => {
  const { t } = useTranslation('pmDetails');
  const editor = useEditor({
    extensions: DESCRIPTION_EXTENSIONS,
    content: toEditorHtml(value),
    autofocus: autoFocus ? 'end' : false,
    immediatelyRender: true,
    editorProps: {
      attributes: {
        id,
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-label': label,
        'data-automation-id': 'details-about-editor',
      },
    },
    onUpdate: ({ editor: current }) => {
      const html = current.getHTML();
      onChange(descriptionText(html) === '' ? '' : html);
    },
  });
  const active = useEditorState({
    editor,
    selector: ({ editor: current }) => ({
      bold: current?.isActive('bold') ?? false,
      list: current?.isActive('bulletList') ?? false,
      link: current?.isActive('link') ?? false,
    }),
  });

  const [linkAnchor, setLinkAnchor] = useState<HTMLElement | null>(null);
  const [linkUrl, setLinkUrl] = useState('');

  const openLink = (anchor: HTMLElement) => {
    setLinkUrl((editor?.getAttributes('link').href as string | undefined) ?? '');
    setLinkAnchor(anchor);
  };
  const closeLink = () => {
    setLinkAnchor(null);
    editor?.commands.focus();
  };
  const applyLink = () => {
    if (!editor) return;
    const chain = editor.chain().focus().extendMarkRange('link');
    if (linkUrl.trim() === '') {
      chain.unsetLink().run();
    } else if (editor.state.selection.empty && !editor.isActive('link')) {
      // Nothing selected: the address becomes the link's text.
      const href = withScheme(linkUrl);
      chain
        .insertContent({
          type: 'text',
          text: linkUrl.trim(),
          marks: [{ type: 'link', attrs: { href } }],
        })
        .run();
    } else {
      chain.setLink({ href: withScheme(linkUrl) }).run();
    }
    setLinkAnchor(null);
  };

  const tool = (
    name: string,
    icon: string,
    pressed: boolean,
    onClick: (anchor: HTMLElement) => void,
  ) => (
    <Tooltip title={name}>
      <IconButton
        size="small"
        aria-label={name}
        aria-pressed={pressed}
        // Keep the editor's selection: act on mouse down, before focus moves.
        onMouseDown={(event) => event.preventDefault()}
        onClick={(event) => onClick(event.currentTarget)}
        data-automation-id={`details-format-${icon}`}
      >
        <MaterialSymbol name={icon} size={20} />
      </IconButton>
    </Tooltip>
  );

  return (
    <Box
      sx={{
        border: 'var(--border-width-thin) solid var(--color-fog-40)',
        borderRadius: 'var(--border-radius-medium)',
        bgcolor: 'var(--color-black-00)',
        '&:focus-within': { borderColor: 'var(--color-blue-80)' },
      }}
    >
      <Stack
        direction="row"
        sx={{
          gap: 0.5,
          px: 1,
          py: 0.5,
          borderBottom: 'var(--border-width-thin) solid var(--color-fog-20)',
        }}
      >
        {tool(t('about.Bold'), 'format_bold', active?.bold ?? false, () =>
          editor?.chain().focus().toggleBold().run(),
        )}
        {tool(t('about.BulletList'), 'format_list_bulleted', active?.list ?? false, () =>
          editor?.chain().focus().toggleBulletList().run(),
        )}
        {tool(t('about.Link'), 'link', active?.link ?? false, openLink)}
      </Stack>
      <Box
        sx={{
          ...DESCRIPTION_CONTENT_SX,
          minHeight: 120,
          maxHeight: 320,
          overflow: 'auto',
          p: 1.5,
          '& .ProseMirror': { minHeight: 96 },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
      <Popover
        open={linkAnchor !== null}
        anchorEl={linkAnchor}
        onClose={closeLink}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 2, width: 320 } } }}
      >
        <Stack sx={{ gap: 1.5 }}>
          <TextField
            autoFocus
            size="small"
            sx={FIELD_HEIGHT_SX}
            value={linkUrl}
            placeholder="https://"
            onChange={(event) => setLinkUrl(event.target.value)}
            onKeyDown={(event: KeyboardEvent) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                applyLink();
              }
            }}
            slotProps={{
              htmlInput: {
                'aria-label': t('about.LinkUrl'),
                'data-automation-id': 'details-format-link-url',
              },
            }}
          />
          <Stack direction="row" sx={{ justifyContent: 'flex-end', gap: 1 }}>
            <Button variant="text" color="secondary" onClick={closeLink}>
              {t('card.Cancel')}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={applyLink}
              data-automation-id="details-format-link-apply"
            >
              {t('about.LinkApply')}
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </Box>
  );
};
